import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  ICreateChatGroupModel,
  IGroupChatFilterModel,
  IGroupMemberModel,
  IRemoveChatGroupMember,
  ISendMessageModel,
  IUpdateChatGroupModel,
  IUpdateMemberStatus,
  ISrListGroupListServerResponse,
  IChatGroupMembersServerResponse,
  ISendMessageServerResponse,
  IInternalMessageServerResponse,
  IGroupListServerResponse,
  IMessageStatusModel,
  ILoadGroupChatPagePayload,
  IUpdateGroupInfoPayload,
  IFileInfoServerResponse,
  INewMessageModel,
} from '../model/internalGroupChat.model';
import { IServerResponse } from '../model/add-view-call-flow.model';
import { SignalrService } from '../sse/signalr.service';
import { EventConstant } from '../sse/event.constant';
import { ILoggedInUserViewModel } from '../model/common.model';
import { RoleName } from '../@shared/constants/Constant';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class InternalGroupChatService {
  // todo all of these Subjects need to be refactored (removed from service)
  public loadGroupChatSubject$ = new BehaviorSubject<ILoadGroupChatPagePayload>(null);
  public loadGroupChatAction = this.loadGroupChatSubject$.asObservable();
  public updateGroupInfoSubject$ = new BehaviorSubject<IUpdateGroupInfoPayload>(null);
  public updateGroupInfoAction = this.updateGroupInfoSubject$.asObservable();
  public exitOrDeleteGroupSubject$ = new BehaviorSubject<ILoadGroupChatPagePayload>(null);
  public exitOrDeleteGroupAction = this.exitOrDeleteGroupSubject$.asObservable();
  public toggleBetweenChatListAndMessagePanelSubject$ = new BehaviorSubject<boolean>(false); //TODO: false to show chat list and true to show message panel on small screen
  public toggleBetweenChatListAndMessagePanelAction = this.toggleBetweenChatListAndMessagePanelSubject$.asObservable();
  public newMessageSubject$ = new BehaviorSubject<INewMessageModel>(null);
  public newMessageAction = this.newMessageSubject$.asObservable();
  public user: ILoggedInUserViewModel;

  constructor(
    private httpClient: HttpClient,
    private _signalRService: SignalrService,
    private _router: Router,
    private _toast: ToastrService,
  ) {
    this.user = JSON.parse(localStorage.getItem('user'));
  }

  getAllMessages(chatGroupID: string, loggedInMemberID: string): Observable<IInternalMessageServerResponse> {
    const url = `${environment.apiUrl}/Chat/getAllMessages/${chatGroupID}/${loggedInMemberID}`;

    return this.httpClient.get<IInternalMessageServerResponse>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getGroupMemberDetails(chatGroupID: string): Observable<IChatGroupMembersServerResponse> {
    const url = `${environment.apiUrl}/Chat/GetGroupMemberDetails/${chatGroupID}`;

    return this.httpClient
      .get<IChatGroupMembersServerResponse>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllUploadedFiles(fileNameList: string[]): Observable<IFileInfoServerResponse> {
    const url = `${environment.apiUrl}/Chat/GetAllUploadedFiles`;

    return this.httpClient
      .post<IFileInfoServerResponse>(url, fileNameList)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getSRListGroupList(getSRListGroupListPayload: IGroupChatFilterModel): Observable<ISrListGroupListServerResponse> {
    const url = `${environment.apiUrl}/Chat/GetChatGroupList`;

    return this.httpClient
      .post<ISrListGroupListServerResponse>(url, getSRListGroupListPayload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  sendMessage(messagePayload: ISendMessageModel): Observable<ISendMessageServerResponse> {
    const url = `${environment.apiUrl}/Chat/SendMessage`;

    return this.httpClient
      .post<ISendMessageServerResponse>(url, messagePayload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  markAllMessagesRead(readAllMessagePayload: IMessageStatusModel): Observable<IServerResponse> {
    const url = `${environment.apiUrl}/Chat/MarkAllMessagesAsRead`;

    return this.httpClient
      .post<IServerResponse>(url, readAllMessagePayload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  createGroupChat(createGroupPayload: ICreateChatGroupModel): Observable<IGroupListServerResponse> {
    const url = `${environment.apiUrl}/Chat/CreateChatGroup`;

    return this.httpClient
      .post<IGroupListServerResponse>(url, createGroupPayload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  addGroupMember(
    addGroupMemberPayload: IGroupMemberModel[],
    chatGroupID: string,
    updatedBy: string,
  ): Observable<IChatGroupMembersServerResponse> {
    const url = `${environment.apiUrl}/Chat/AddGroupMember/${chatGroupID}/${updatedBy}`;

    return this.httpClient
      .post<IChatGroupMembersServerResponse>(url, addGroupMemberPayload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  removeGroupMember(removeGroupMemberPayload: IRemoveChatGroupMember): Observable<IServerResponse> {
    const url = `${environment.apiUrl}/Chat/RemoveGroupMember`;

    return this.httpClient
      .post<IServerResponse>(url, removeGroupMemberPayload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updateGroupChat(updateGroupPayload: IUpdateChatGroupModel): Observable<IServerResponse> {
    const url = `${environment.apiUrl}/Chat/UpdateChatGroup`;

    return this.httpClient
      .put<IServerResponse>(url, updateGroupPayload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updateGroupMemberStatus(updateMemberStatusPayload: IUpdateMemberStatus): Observable<IServerResponse> {
    const url = `${environment.apiUrl}/Chat/UpdateGroupMemberStatus`;

    return this.httpClient
      .put<IServerResponse>(url, updateMemberStatusPayload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  deleteGroupChat(groupChatID: string, updatedByMemberID: string): Observable<IServerResponse> {
    const url = `${environment.apiUrl}/Chat/DeleteChatGroup/${groupChatID}/${updatedByMemberID}`;

    return this.httpClient.delete<IServerResponse>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  messageEventListener() {
    // todo this functionality is being performed in the wrong place and will be refactored later
    if (this.user.role !== RoleName.InternalReadOnly) {
      this._signalRService.sseEventListener(EventConstant.internalGroupChatSendMessageEvent);
      this._signalRService.eventDataChange.subscribe(
        (value) => {
          if (value.eventName === EventConstant.internalGroupChatSendMessageEvent) {
            if (this._router.url !== '/group-chat') {
              this._toast
                .info('You got some new message', 'New Message', {
                  timeOut: 6000,
                  positionClass: 'toast-bottom-right',
                })
                .onTap.subscribe(() => this.redirectToGroupChatPage());
            }
            this.newMessageSubject$.next(value.data as INewMessageModel);
          }
        },
        (error) => {
          console.log(error);
        },
      );
    }
    return null;
  }

  redirectToGroupChatPage() {
    // todo this action is being performed in the wrong place and will be refactored later
    if (this._router.url !== '/group-chat') {
      this._router.navigate(['/group-chat']);
    }
  }
}
